// Contextual sidebar state — docs/Menus-Management/menu-design.md §17, §19, §65.
//
// The sidebar renders fully expanded from the server so it is usable without
// JavaScript. This hook restores and persists the two pieces of state the spec
// calls for — `sidebarCollapsed` and `expandedMenuGroups` — in localStorage, so
// they survive navigation and reload without a round trip.
//
// Collapsed groups are stored per module, since group names are only unique
// within a module.

const COLLAPSED_KEY = "nav:sidebar-collapsed";
const GROUPS_KEY = "nav:collapsed-groups";
const SCROLL_KEY = "nav:sidebar-scroll";
const MAIN_PADDING_CLASS = "lg:pl-60";
const MAIN_PADDING_COLLAPSED_CLASS = "lg:pl-16";

const readJSON = (key, fallback) => {
  try {
    const raw = window.localStorage.getItem(key);
    return raw ? JSON.parse(raw) : fallback;
  } catch (_e) {
    return fallback;
  }
};

const writeJSON = (key, value) => {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
  } catch (_e) {
    // Private browsing or a full quota — state simply does not persist.
  }
};

const NavSidebar = {
  mounted() {
    this.toggleBtn = this.el.querySelector("[data-sidebar-toggle]");
    this.navEl = this.el.querySelector("nav");

    if (this.toggleBtn) {
      this.onToggle = this.toggleCollapsed.bind(this);
      this.toggleBtn.addEventListener("click", this.onToggle);
    }

    this.onGroupToggle = this.persistGroups.bind(this);
    this.el.addEventListener("toggle", this.onGroupToggle, true);

    // Clicking a menu item does a LiveView `navigate` to a different route
    // (often a different LiveView entirely), which re-renders this sidebar
    // from the root layout and drops its scroll position. Persist it here —
    // same mechanism as the collapsed-groups state below — and reapply it
    // on every mount/update so the list stays where the user left it.
    if (this.navEl) {
      this.onScroll = this.persistScroll.bind(this);
      this.navEl.addEventListener("scroll", this.onScroll, { passive: true });
    }

    this.restore();
  },

  updated() {
    // A module switch re-renders the group list; re-apply that module's state.
    this.restore();
  },

  destroyed() {
    if (this.toggleBtn) this.toggleBtn.removeEventListener("click", this.onToggle);
    this.el.removeEventListener("toggle", this.onGroupToggle, true);
    if (this.navEl) this.navEl.removeEventListener("scroll", this.onScroll);
  },

  moduleId() {
    return this.el.dataset.module || "unknown";
  },

  groups() {
    return Array.from(this.el.querySelectorAll("[data-menu-group]"));
  },

  restore() {
    this.applyCollapsed(readJSON(COLLAPSED_KEY, false) === true);

    const collapsedGroups = readJSON(GROUPS_KEY, {})[this.moduleId()] || [];

    this.groups().forEach((group) => {
      group.open = !collapsedGroups.includes(group.dataset.menuGroup);
    });

    // Group open/close above changes content height, so restore scroll after
    // it — and on the next frame, once the browser has actually laid out
    // that new height, or a tall module would clamp scrollTop back to 0.
    requestAnimationFrame(() => this.restoreScroll());
  },

  scrollKey() {
    return `${SCROLL_KEY}:${this.moduleId()}`;
  },

  persistScroll() {
    if (!this.navEl) return;
    writeJSON(this.scrollKey(), this.navEl.scrollTop);
  },

  restoreScroll() {
    if (!this.navEl) return;
    const saved = readJSON(this.scrollKey(), 0);
    if (saved > 0) this.navEl.scrollTop = saved;
  },

  toggleCollapsed() {
    const next = !this.el.hasAttribute("data-collapsed");
    this.applyCollapsed(next);
    writeJSON(COLLAPSED_KEY, next);
  },

  applyCollapsed(collapsed) {
    if (collapsed) {
      this.el.setAttribute("data-collapsed", "");
    } else {
      this.el.removeAttribute("data-collapsed");
    }

    if (this.toggleBtn) {
      this.toggleBtn.setAttribute("aria-expanded", String(!collapsed));
      this.toggleBtn.title = collapsed ? "Expand navigation" : "Collapse navigation";
    }

    // Keep the main content flush against the sidebar as it resizes.
    const main = document.getElementById("main-region");
    if (main) {
      main.classList.toggle(MAIN_PADDING_CLASS, !collapsed);
      main.classList.toggle(MAIN_PADDING_COLLAPSED_CLASS, collapsed);
    }

    // A collapsed sidebar shows icons only, so open/closed groups are
    // meaningless there — force them open to keep every icon reachable (§19).
    if (collapsed) {
      this.groups().forEach((group) => {
        group.open = true;
      });
    }
  },

  persistGroups(event) {
    if (!event.target.matches("[data-menu-group]")) return;
    if (this.el.hasAttribute("data-collapsed")) return;

    const collapsed = this.groups()
      .filter((group) => !group.open)
      .map((group) => group.dataset.menuGroup);

    const all = readJSON(GROUPS_KEY, {});
    all[this.moduleId()] = collapsed;
    writeJSON(GROUPS_KEY, all);
  },
};

export default NavSidebar;
